import React, { useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';

import { Input } from '../lumen';

const ALL_WORKSPACES = 'all';

const rank = (name, query) => {
  const index = name.toLowerCase().indexOf(query);
  return index === 0 ? 0 : 1;
};

export const WorkspacePicker = ({ workspaces = [], onSelect, onClose }) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);

  const { matches, noResults } = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { matches: workspaces, noResults: false };
    const pinned = workspaces.filter(w => w.workspace_id === ALL_WORKSPACES);
    const found = workspaces
      .filter(w => w.workspace_id !== ALL_WORKSPACES)
      .filter(w => w.name.toLowerCase().includes(q))
      .sort((a, b) => rank(a.name, q) - rank(b.name, q));
    return { matches: [...pinned, ...found], noResults: found.length === 0 };
  }, [workspaces, query]);

  const clampedIndex = Math.min(activeIndex, Math.max(matches.length - 1, 0));

  const select = workspace => {
    if (!workspace) return;
    onSelect?.(workspace.workspace_id);
    onClose?.();
  };

  const moveTo = index => {
    setActiveIndex(index);
    listRef.current
      ?.querySelector(`[data-index="${index}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  };

  const handleKeyDown = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveTo(Math.min(clampedIndex + 1, matches.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveTo(Math.max(clampedIndex - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      select(matches[clampedIndex]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose?.();
    }
  };

  return (
    <Root onKeyDown={handleKeyDown} data-test-id="workspace-picker">
      <SearchRow>
        <Input
          icon="search"
          autoFocus
          placeholder="Search workspace"
          aria-label="Search workspace"
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            setActiveIndex(0);
          }}
        />
      </SearchRow>
      <List ref={listRef} role="listbox">
        {matches.map((workspace, index) => (
          <Item
            key={workspace.workspace_id}
            type="button"
            role="option"
            data-index={index}
            aria-selected={index === clampedIndex}
            $active={index === clampedIndex}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => select(workspace)}
          >
            <ItemName>{workspace.name}</ItemName>
            {workspace.project_count != null && (
              <ProjectCount>({workspace.project_count})</ProjectCount>
            )}
          </Item>
        ))}
        {noResults && <Empty>No workspaces match</Empty>}
      </List>
    </Root>
  );
};

const Root = styled.div`
  width: 260px;
  display: flex;
  flex-direction: column;
`;

const SearchRow = styled.div`
  padding: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.color.slate[200]};

  input {
    height: 32px;
    border-color: ${({ theme }) => theme.color.slate[500]};
  }
`;

const List = styled.div`
  max-height: 320px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px 0;
`;

const Item = styled.button`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.fg.default};
  background: ${({ $active, theme }) =>
    $active ? theme.color.bg.subtle : 'transparent'};
  border: 0;
  cursor: pointer;
  text-align: left;
`;

const ItemName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProjectCount = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.fg.subtle};
`;

const Empty = styled.div`
  padding: 16px;
  font-size: 14px;
  text-align: center;
  color: ${({ theme }) => theme.color.fg.muted};
`;
